/*
 * FHF hero slideshow: cross-fading slides with arrows, dots and optional auto-rotate.
 * Auto-rotate pauses on hover/focus, stops for prefers-reduced-motion, and jumps to a
 * slide when it is selected in the theme editor.
 */
if (!customElements.get('fhf-hero')) {
  customElements.define(
    'fhf-hero',
    class FhfHero extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('.fhf-hero__slide')];
        this.dots = [...this.querySelectorAll('.fhf-hero__dot')];
        this.index = 0;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.syncVideos();
        // swap landscape/vertical if the window crosses the phone breakpoint
        window.matchMedia('(max-width: 749px)').addEventListener('change', () => this.syncVideos());
        if (this.slides.length < 2) return;

        this.querySelectorAll('.fhf-hero__arrow').forEach((btn) =>
          btn.addEventListener('click', () => this.go(this.index + Number(btn.dataset.dir), true))
        );
        this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.go), true)));

        this.autoplay = this.dataset.autoplay === 'true' && !this.reduceMotion;
        this.delay = Number(this.dataset.speed || 7) * 1000;
        this.paused = false;
        this.addEventListener('mouseenter', () => (this.paused = true));
        this.addEventListener('mouseleave', () => (this.paused = false));
        this.addEventListener('focusin', () => (this.paused = true));
        this.addEventListener('focusout', () => (this.paused = false));
        this.start();

        this.onBlockSelect = (event) => {
          const i = this.slides.indexOf(event.target);
          if (i > -1) {
            this.stop();
            this.go(i);
          }
        };
        this.onBlockDeselect = () => this.start();
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      disconnectedCallback() {
        this.stop();
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      start() {
        this.stop();
        if (!this.autoplay) return;
        this.timer = setInterval(() => {
          if (!this.paused && !document.hidden) this.go(this.index + 1);
        }, this.delay);
      }

      stop() {
        clearInterval(this.timer);
      }

      // background videos: only the visible slide plays; none play with reduced motion
      syncVideos() {
        this.slides.forEach((slide, j) => {
          const video = slide.querySelector('.fhf-hero__video');
          if (!video) return;
          if (j === this.index && !this.reduceMotion) {
            // pick the vertical file on phones (falls back to whichever one is set), loaded only when needed
            const mobile = window.matchMedia('(max-width: 749px)').matches;
            const src = mobile
              ? video.dataset.srcMobile || video.dataset.srcDesktop
              : video.dataset.srcDesktop || video.dataset.srcMobile;
            if (!src) return;
            if (video.getAttribute('src') !== src) {
              video.classList.remove('is-playing');
              video.src = src;
            }
            video.muted = true;
            const played = video.play();
            if (played && played.catch) played.catch(() => {});
            video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
          } else {
            video.pause();
          }
        });
      }

      go(i, userInitiated = false) {
        const n = this.slides.length;
        const next = ((i % n) + n) % n;
        if (next === this.index) return;
        this.slides.forEach((slide, j) => {
          const active = j === next;
          slide.classList.toggle('is-active', active);
          slide.toggleAttribute('inert', !active);
          if (active) slide.removeAttribute('aria-hidden');
          else slide.setAttribute('aria-hidden', 'true');
        });
        this.dots.forEach((dot, j) => {
          if (j === next) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        this.index = next;
        this.syncVideos();
        if (userInitiated) this.start(); // restart the timer so a click gets a full slide
      }
    }
  );
}
